/**
 * 会话初始化守卫
 *
 * 仅在应用首次导航时执行一次，负责调用 authStore.initSession()
 * 恢复持久化的会话状态。如果初始化失败，不阻塞导航，
 * 后续的 authCheckGuard 会处理未认证状态。
 */

import type { NavigationGuardWithThis } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

/** 标记会话是否已完成初始化 */
let sessionInitialized = false;

/**
 * 会话初始化守卫。
 *
 * - 只在第一次导航时执行 `initSession()`
 * - initSession 内部有防重入保护，多次调用会复用同一个 Promise
 * - 如果初始化失败，静默处理，让导航继续
 */
export const initSessionGuard: NavigationGuardWithThis<undefined> = async () => {
  if (sessionInitialized) {
    return;
  }

  const authStore = useAuthStore();

  if (authStore.isLoadingSession) {
    try {
      await authStore.initSession();
    } catch {
      // 初始化失败不阻塞导航；后续 authCheckGuard 会处理未认证状态
    }
  }

  // 无论成功失败，都标记为已初始化（initSession 内部有防重入，重复调用无害）
  sessionInitialized = true;
};
